import { Type, type Static } from "@sinclair/typebox";
import { UuidSchema } from "./identifiers.ts";

export const DOCUMENT_TAG_SCHEMA_VERSION = "1.0" as const;
export const DOCUMENT_TAG_MAX_TEXT_LENGTH = 4_096;
export const DOCUMENT_TAG_MAX_LIST_ITEMS = 128;
export const DOCUMENT_TAG_MAX_DEFINITIONS_PER_FILE_SPACE = 64;
export const DOCUMENT_TAG_BULK_MAX_DOCUMENTS = 100;
export const DOCUMENT_TAG_BULK_MAX_ASSIGNMENTS = 16;
export const DOCUMENT_TAG_EXTRACTION_MAX_DEFINITIONS = 16;
export const DOCUMENT_TAG_EXTRACTION_MAX_DOCUMENTS = 50;
export const DOCUMENT_TAG_EXTRACTION_MAX_DOCUMENT_CHARACTERS = 100_000;
export const DOCUMENT_TAG_EXTRACTION_MAX_TOTAL_CHARACTERS = 500_000;

const TimestampSchema = Type.String({ format: "date-time" });
export const DocumentTagKeySchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[a-z][a-z0-9_]{0,63}$"
});
export type DocumentTagKeyV1 = Static<typeof DocumentTagKeySchema>;

export const DocumentTagValueTypeSchema = Type.Union([
  Type.Literal("text"),
  Type.Literal("number"),
  Type.Literal("boolean"),
  Type.Literal("date"),
  Type.Literal("datetime"),
  Type.Literal("text_list"),
  Type.Literal("number_list")
]);
export type DocumentTagValueTypeV1 = Static<typeof DocumentTagValueTypeSchema>;

export const DocumentTagValueSchema = Type.Union([
  Type.Object(
    {
      type: Type.Literal("text"),
      value: Type.String({ minLength: 1, maxLength: DOCUMENT_TAG_MAX_TEXT_LENGTH })
    },
    { additionalProperties: false }
  ),
  Type.Object(
    { type: Type.Literal("number"), value: Type.Number() },
    { additionalProperties: false }
  ),
  Type.Object(
    { type: Type.Literal("boolean"), value: Type.Boolean() },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      type: Type.Literal("date"),
      value: Type.String({ format: "date", pattern: "^\\d{4}-\\d{2}-\\d{2}$" })
    },
    { additionalProperties: false }
  ),
  Type.Object(
    { type: Type.Literal("datetime"), value: TimestampSchema },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      type: Type.Literal("text_list"),
      value: Type.Array(Type.String({ minLength: 1, maxLength: 1_024 }), {
        minItems: 1,
        maxItems: DOCUMENT_TAG_MAX_LIST_ITEMS,
        uniqueItems: true
      })
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      type: Type.Literal("number_list"),
      value: Type.Array(Type.Number(), {
        minItems: 1,
        maxItems: DOCUMENT_TAG_MAX_LIST_ITEMS,
        uniqueItems: true
      })
    },
    { additionalProperties: false }
  )
]);
export type DocumentTagValueV1 = Static<typeof DocumentTagValueSchema>;

export const DocumentTagDefinitionSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_TAG_SCHEMA_VERSION),
    tagDefinitionId: UuidSchema,
    fileSpaceId: UuidSchema,
    key: DocumentTagKeySchema,
    name: Type.String({ minLength: 1, maxLength: 256 }),
    description: Type.Union([Type.String({ minLength: 1, maxLength: 4_096 }), Type.Null()]),
    valueType: DocumentTagValueTypeSchema,
    createdByPrincipalId: UuidSchema,
    revision: Type.Integer({ minimum: 0 }),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type DocumentTagDefinitionV1 = Static<typeof DocumentTagDefinitionSchema>;

export const DocumentTagDefinitionCreateInputSchema = Type.Object(
  {
    key: DocumentTagKeySchema,
    name: Type.String({ minLength: 1, maxLength: 256 }),
    description: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 4_096 }), Type.Null()])
    ),
    valueType: DocumentTagValueTypeSchema
  },
  { additionalProperties: false }
);
export type DocumentTagDefinitionCreateInputV1 = Static<
  typeof DocumentTagDefinitionCreateInputSchema
>;

export const DocumentTagDefinitionUpdateInputSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 256 }),
    description: Type.Union([Type.String({ minLength: 1, maxLength: 4_096 }), Type.Null()]),
    expectedRevision: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);
export type DocumentTagDefinitionUpdateInputV1 = Static<
  typeof DocumentTagDefinitionUpdateInputSchema
>;

export const DocumentTagSourceSchema = Type.Union([Type.Literal("manual"), Type.Literal("ai")]);
export type DocumentTagSourceV1 = Static<typeof DocumentTagSourceSchema>;

export const DocumentTagSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_TAG_SCHEMA_VERSION),
    tagId: UuidSchema,
    fileSpaceId: UuidSchema,
    documentId: UuidSchema,
    tagDefinitionId: UuidSchema,
    value: DocumentTagValueSchema,
    source: DocumentTagSourceSchema,
    setByPrincipalId: UuidSchema,
    extractionRunId: Type.Union([UuidSchema, Type.Null()]),
    revision: Type.Integer({ minimum: 0 }),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type DocumentTagV1 = Static<typeof DocumentTagSchema>;

export const DocumentTagPutInputSchema = Type.Object(
  {
    expectedRevision: Type.Union([Type.Null(), Type.Integer({ minimum: 0 })]),
    value: DocumentTagValueSchema
  },
  { additionalProperties: false }
);
export type DocumentTagPutInputV1 = Static<typeof DocumentTagPutInputSchema>;

export const DocumentTagDeleteInputSchema = Type.Object(
  { expectedRevision: Type.Integer({ minimum: 0 }) },
  { additionalProperties: false }
);
export type DocumentTagDeleteInputV1 = Static<typeof DocumentTagDeleteInputSchema>;

export const DocumentTagAssignmentSchema = Type.Object(
  { tagDefinitionId: UuidSchema, value: DocumentTagValueSchema },
  { additionalProperties: false }
);
export type DocumentTagAssignmentV1 = Static<typeof DocumentTagAssignmentSchema>;

export const DocumentTagCollectionAssignmentInputSchema = Type.Object(
  {
    assignments: Type.Array(DocumentTagAssignmentSchema, {
      minItems: 1,
      maxItems: DOCUMENT_TAG_BULK_MAX_ASSIGNMENTS
    })
  },
  { additionalProperties: false }
);
export type DocumentTagCollectionAssignmentInputV1 = Static<
  typeof DocumentTagCollectionAssignmentInputSchema
>;

export const DocumentTagCollectionAssignmentResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_TAG_SCHEMA_VERSION),
    appliedDocumentCount: Type.Integer({ minimum: 0 }),
    appliedTagCount: Type.Integer({ minimum: 0 }),
    skippedDocumentCount: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);
export type DocumentTagCollectionAssignmentResultV1 = Static<
  typeof DocumentTagCollectionAssignmentResultSchema
>;

export const DocumentTagExtractionRunStatusSchema = Type.Union([
  Type.Literal("running"),
  Type.Literal("succeeded"),
  Type.Literal("partial"),
  Type.Literal("failed")
]);
export type DocumentTagExtractionRunStatusV1 = Static<
  typeof DocumentTagExtractionRunStatusSchema
>;

export const DocumentTagExtractionResultOutcomeSchema = Type.Union([
  Type.Literal("set"),
  Type.Literal("cleared"),
  Type.Literal("skipped_manual"),
  Type.Literal("skipped_input"),
  Type.Literal("failed")
]);
export type DocumentTagExtractionResultOutcomeV1 = Static<
  typeof DocumentTagExtractionResultOutcomeSchema
>;

export const DocumentTagExtractionRunSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_TAG_SCHEMA_VERSION),
    runId: UuidSchema,
    fileSpaceId: UuidSchema,
    collectionId: UuidSchema,
    requestedByPrincipalId: UuidSchema,
    status: DocumentTagExtractionRunStatusSchema,
    requestedDefinitionCount: Type.Integer({ minimum: 1 }),
    requestedDocumentCount: Type.Integer({ minimum: 0 }),
    succeededResultCount: Type.Integer({ minimum: 0 }),
    skippedResultCount: Type.Integer({ minimum: 0 }),
    failedResultCount: Type.Integer({ minimum: 0 }),
    errorCode: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    errorMessage: Type.Union([Type.String({ minLength: 1, maxLength: 2_048 }), Type.Null()]),
    startedAt: TimestampSchema,
    completedAt: Type.Union([TimestampSchema, Type.Null()])
  },
  { additionalProperties: false }
);
export type DocumentTagExtractionRunV1 = Static<typeof DocumentTagExtractionRunSchema>;

export const DocumentTagExtractionResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(DOCUMENT_TAG_SCHEMA_VERSION),
    runId: UuidSchema,
    fileSpaceId: UuidSchema,
    documentId: UuidSchema,
    tagDefinitionId: UuidSchema,
    outcome: DocumentTagExtractionResultOutcomeSchema,
    extractedValue: Type.Union([DocumentTagValueSchema, Type.Null()]),
    errorCode: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    errorMessage: Type.Union([Type.String({ minLength: 1, maxLength: 2_048 }), Type.Null()]),
    createdAt: TimestampSchema
  },
  { additionalProperties: false }
);
export type DocumentTagExtractionResultV1 = Static<typeof DocumentTagExtractionResultSchema>;

export const DocumentTagExtractionRunInputSchema = Type.Object(
  {
    tagDefinitionIds: Type.Array(UuidSchema, {
      minItems: 1,
      maxItems: DOCUMENT_TAG_EXTRACTION_MAX_DEFINITIONS,
      uniqueItems: true
    })
  },
  { additionalProperties: false }
);
export type DocumentTagExtractionRunInputV1 = Static<typeof DocumentTagExtractionRunInputSchema>;

export const DocumentTagExtractionRunDetailSchema = Type.Object(
  {
    run: DocumentTagExtractionRunSchema,
    results: Type.Array(DocumentTagExtractionResultSchema, {
      maxItems: DOCUMENT_TAG_EXTRACTION_MAX_DOCUMENTS * DOCUMENT_TAG_EXTRACTION_MAX_DEFINITIONS
    })
  },
  { additionalProperties: false }
);
export type DocumentTagExtractionRunDetailV1 = Static<
  typeof DocumentTagExtractionRunDetailSchema
>;

export const DocumentTagModelExtractionSchema = Type.Object(
  {
    values: Type.Array(
      Type.Object(
        {
          key: DocumentTagKeySchema,
          value: Type.Union([DocumentTagValueSchema, Type.Null()])
        },
        { additionalProperties: false }
      ),
      { maxItems: DOCUMENT_TAG_EXTRACTION_MAX_DEFINITIONS }
    )
  },
  { additionalProperties: false }
);
export type DocumentTagModelExtractionV1 = Static<typeof DocumentTagModelExtractionSchema>;
